/**
 * Repository Scan Results Component
 *
 * Displays detailed scan results for a selected repository
 *
 * Features:
 * - Scan history timeline
 * - Issue categorization (security, quality, dependencies)
 * - Severity filtering
 * - Issue details with remediation suggestions
 * - Trend analysis
 *
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Button, Spinner, Alert, Tabs } from 'flowbite-react';
import { HiShieldExclamation, HiCode, HiCube, HiTrendingUp, HiArrowLeft } from 'react-icons/hi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface ScanResult {
  Scan_ID: string;
  Repository_ID: string;
  Scan_Date: string;
  Status: string; // 'completed' | 'in_progress' | 'failed'
  Total_Issues: number;
  Critical_Issues: number;
  High_Issues: number;
  Medium_Issues: number;
  Low_Issues: number;
  Security_Issues: number;
  Quality_Issues: number;
  Dependency_Issues: number;
}

interface Issue {
  Issue_ID: string;
  Scan_ID: string;
  Category: string; // 'security' | 'quality' | 'dependency'
  Severity: string; // 'critical' | 'high' | 'medium' | 'low'
  Title: string;
  Description: string;
  File_Path: string;
  Line_Number?: number;
  Remediation?: string;
  CVE_ID?: string;
}

interface RepositoryScanResultsProps {
  repositoryId: string;
  clientId: string;
  companyId: string;
  onBack?: () => void;
}

export const RepositoryScanResults: React.FC<RepositoryScanResultsProps> = ({
  repositoryId,
  clientId,
  companyId,
  onBack
}) => {
  const [scans, setScans] = useState<ScanResult[]>([]);
  const [selectedScan, setSelectedScan] = useState<string | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>('all');

  const fetchScans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await Make_Authorized_API_Call<ScanResult>(
        API_ENDPOINTS.REPOSITORIES.SCANS,
        'GET',
        undefined,
        { params: { Repository_ID: repositoryId, Client_ID: clientId, Company_ID: companyId } }
      );

      if (isSuccessResponse(response)) {
        const scanResults = response.data?.Records || [];
        setScans(scanResults);

        // Auto-select the most recent scan
        if (scanResults.length > 0 && !selectedScan) {
          setSelectedScan(scanResults[0].Scan_ID);
        }
      } else {
        throw new Error(response.message || 'API returned unsuccessful response');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load scan results';
      console.error('[RepositoryScanResults] Error fetching scans:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [repositoryId, clientId, companyId, selectedScan]);

  const fetchIssues = useCallback(async (scanId: string) => {
    try {
      setIssuesLoading(true);

      const response = await Make_Authorized_API_Call<Issue>(
        API_ENDPOINTS.REPOSITORIES.SCAN_FINDINGS,
        'GET',
        undefined,
        { params: { Scan_ID: scanId, Client_ID: clientId, Company_ID: companyId } }
      );

      if (isSuccessResponse(response)) {
        setIssues(response.data?.Records || []);
      } else {
        throw new Error(response.message || 'API returned unsuccessful response');
      }
    } catch (err) {
      console.error('[RepositoryScanResults] Error fetching issues:', err);
    } finally {
      setIssuesLoading(false);
    }
  }, [clientId, companyId]);

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  useEffect(() => {
    if (selectedScan) {
      fetchIssues(selectedScan);
    }
  }, [selectedScan, fetchIssues]);

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'failure';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'security':
        return HiShieldExclamation;
      case 'quality':
        return HiCode;
      case 'dependency':
        return HiCube;
      default:
        return HiCode;
    }
  };

  const filteredIssues = severityFilter === 'all'
    ? issues
    : issues.filter(issue => issue.Severity.toLowerCase() === severityFilter.toLowerCase());

  const currentScan = scans.find(scan => scan.Scan_ID === selectedScan);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="failure">
        <div>
          <p className="font-semibold">Error loading scan results</p>
          <p className="text-sm mt-1">{error}</p>
          <Button
            size="xs"
            color="light"
            className="mt-2"
            onClick={fetchScans}
          >
            Retry
          </Button>
        </div>
      </Alert>
    );
  }

  if (scans.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <HiShieldExclamation className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No scans available</p>
          <p className="text-sm text-gray-400">
            Run a scan to see security and quality issues
          </p>
          {onBack && (
            <Button
              color="light"
              className="mt-4"
              onClick={onBack}
            >
              <HiArrowLeft className="w-4 h-4 mr-2" />
              Back to Repositories
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        {onBack && (
          <Button
            color="light"
            size="sm"
            onClick={onBack}
          >
            <HiArrowLeft className="w-4 h-4 mr-2" />
            Back to Repositories
          </Button>
        )}
      </div>

      {/* Scan Summary */}
      {currentScan && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Issues</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {currentScan.Total_Issues}
              </p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-gray-500">Security</p>
              <p className="text-3xl font-bold text-red-600 mt-1">
                {currentScan.Security_Issues}
              </p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-gray-500">Quality</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">
                {currentScan.Quality_Issues}
              </p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-sm text-gray-500">Dependencies</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">
                {currentScan.Dependency_Issues}
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Scan History & Severity Filter */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mr-2">Scan:</label>
            <select
              value={selectedScan || ''}
              onChange={(e) => setSelectedScan(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
            >
              {scans.map((scan) => (
                <option key={scan.Scan_ID} value={scan.Scan_ID}>
                  {new Date(scan.Scan_Date).toLocaleString()} - {scan.Total_Issues} issues
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mr-2">Severity:</label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
            >
              <option value="all">All</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Issues List */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">
          Issues ({filteredIssues.length})
        </h3>

        {issuesLoading ? (
          <div className="flex items-center justify-center h-32">
            <Spinner size="lg" />
          </div>
        ) : filteredIssues.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No issues found
          </div>
        ) : (
          <div className="space-y-3">
            {filteredIssues.map((issue) => {
              const CategoryIcon = getCategoryIcon(issue.Category);
              return (
                <div
                  key={issue.Issue_ID}
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <CategoryIcon className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900">{issue.Title}</h4>
                          <Badge color={getSeverityColor(issue.Severity)}>
                            {issue.Severity.toUpperCase()}
                          </Badge>
                          <Badge color="gray">{issue.Category}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{issue.Description}</p>
                        <div className="text-xs text-gray-500 font-mono">
                          {issue.File_Path}
                          {issue.Line_Number && `:${issue.Line_Number}`}
                        </div>
                        {issue.CVE_ID && (
                          <div className="mt-2">
                            <Badge color="warning" size="sm">
                              {issue.CVE_ID}
                            </Badge>
                          </div>
                        )}
                        {issue.Remediation && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                            <strong className="text-blue-900">Remediation:</strong>
                            <p className="text-blue-800 mt-1">{issue.Remediation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};
